import { Form } from '@ant-design/compatible';
import { Col, DatePicker, InputNumber, Row } from 'antd';
import SidePanel from 'common/side-panel/side-panel';
import Spinner from 'common/spinner/components/spinner';
import UsersDropdown from 'common/users/containers/users-dropdown-container';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, Dropdown, FormItem, notification } from 'ui';
import moment from 'utils/cops-moment';
import { withRouter } from 'utils/withRouter';
// eslint-disable-next-line max-len
import { resetSmartActionSuccess } from '../../containers/smart-action-sidepanel/smart-action-sidepanel-actions';

const StyledSaveButton = styled(Button)`
  margin-top: 30px;
  text-transform: uppercase;
  margin-right: 10px;
`;

const StyledInputNumber = styled(InputNumber)`
  width 20%;
`;

const DaysSpan = styled.span`
  margin-left: 5px;
  font-size:  16px;
`;

const FollowUpSidepanel = ({
  match,
  history,
  intl,
  form,
  ticket,
  action,
  saveSmartAction,
  createSmartAction,
  success,
  isSaving,
  fetchSmartAction,
  isLoadingAction,
  followUpTypes = [],
}) => {
  const dispatch = useDispatch();
  const [actionTypeIdState, setActionTypeIdState] = useState(null);
  const [actionTypeDescriptionState, setActionTypeDescriptionState] = useState(null);
  const { actionTypes } = useSelector(state => state.ticketForm);

  useEffect(() => {
    const {
      params: { actionTypeId, actionId },
    } = match;
    setActionTypeIdState(actionTypeId);
    setActionTypeDescriptionState(actionTypes?.find(a => a.id === actionTypeId)?.description || '');
    if (actionId) {
      fetchSmartAction(actionId);
    }
  }, []);

  const goToTicket = () => {
    history.back();
  };

  useEffect(() => {
    if (success) {
      notification.success({
        message: intl.formatMessage({ id: 'ticket.SAVE_SUCCESS' }),
        placement: 'top',
      });
      dispatch(resetSmartActionSuccess());
      goToTicket();
    }
  }, [success]);

  const createAction = () => {
    const type = form.getFieldValue('type');
    const reminder = form.getFieldValue('reminder');
    const followUpBy = form.getFieldValue('followUpBy');
    const manager = form.getFieldValue('manager');
    const followUpByDisplayName = form.getFieldValue('followUpByDisplayName');
    const managerDisplayName = form.getFieldValue('managerDisplayName');
    const managerMail = form.getFieldValue('managerMail');
    const followUpByMail = form.getFieldValue('followUpByMail');
    const since = form.getFieldValue('sinceDate');
    const scope = JSON.parse(localStorage.getItem('serviceCenter'));
    const ticketId = ticket.id;
    createSmartAction({
      actionType: actionTypeIdState,
      type,
      reminder,
      followUpBy,
      manager,
      since,
      scope: scope?.value,
      ticketId,
      followUpByDisplayName,
      managerDisplayName,
      followUpByMail,
      managerMail,
    });
  };

  const saveAction = () => {
    const type = form.getFieldValue('type');
    const reminder = form.getFieldValue('reminder');
    const followUpBy = form.getFieldValue('followUpBy');
    const manager = form.getFieldValue('manager');
    const followUpByDisplayName = form.getFieldValue('followUpByDisplayName');
    const managerDisplayName = form.getFieldValue('managerDisplayName');
    const followUpByMail = form.getFieldValue('followUpByMail');
    const managerMail = form.getFieldValue('managerMail');
    const since = form.getFieldValue('sinceDate');
    const scope = JSON.parse(localStorage.getItem('serviceCenter'));
    const ticketId = ticket.id;

    saveSmartAction({
      id: match.params.actionId,
      type,
      reminder,
      followUpBy,
      manager,
      since,
      scope: scope?.value,
      ticketId,
      followUpByDisplayName,
      managerDisplayName,
      followUpByMail,
      managerMail,
    });
  };

  const handleUserChange = (isManager = false) => {
    const { setFieldsValue } = form;
    if (isManager) {
      setFieldsValue({
        manager: '',
        managerDisplayName: '',
        managerMail: '',
      });
    } else {
      setFieldsValue({
        followUpBy: '',
        followUpByDisplayName: '',
        followUpByMail: '',
      });
    }
  };

  const handleUserSelect = ({ value, selectedUser }, isManager = false) => {
    const { setFieldsValue } = form;
    if (isManager) {
      setFieldsValue({
        manager: value,
        managerDisplayName: selectedUser.displayName,
        managerMail: selectedUser.mail,
      });
    } else {
      setFieldsValue({
        followUpBy: value,
        followUpByDisplayName: selectedUser.displayName,
        followUpByMail: selectedUser.mail,
      });
    }
  };

  const typeValidator = form.getFieldDecorator('type', {
    rules: [
      { required: true, message: intl.formatMessage({ id: 'rules.TYPE' }) },
    ],
    initialValue: action?.type ?? 'technical',
  });
  const sinceValidator = form.getFieldDecorator('sinceDate', {
    rules: [
      { required: true, message: intl.formatMessage({ id: 'rules.REMINDER' }) },
    ],
    initialValue: action?.since ? moment(action.since) : moment(),
  });
  const cycleValidator = form.getFieldDecorator('reminder', {
    rules: [
      { required: true, message: intl.formatMessage({ id: 'rules.REMINDER' }) },
    ],
    initialValue: action?.reminder ?? 1,
  });
  form.getFieldDecorator('followUpBy', {
    rules: [],
    initialValue: action?.followUpBy ?? '',
  });
  form.getFieldDecorator('followUpByMail', {
    rules: [],
    initialValue: action?.followUpByMail ?? '',
  });
  const followedByValidator = form.getFieldDecorator('followUpByDisplayName', {
    rules: [
      { required: true, message: intl.formatMessage({ id: 'rules.FOLLOW_UP_BY' }) },
    ],
    initialValue: action?.followUpByDisplayName ?? '',
  });
  form.getFieldDecorator('manager', {
    rules: [],
    initialValue: action?.manager ?? '',
  });
  form.getFieldDecorator('managerMail', {
    rules: [],
    initialValue: action?.managerMail ?? '',
  });
  const managerValidator = form.getFieldDecorator('managerDisplayName', {
    rules: [
      { required: true, message: intl.formatMessage({ id: 'rules.MANAGER' }) },
    ],
    initialValue: action?.managerDisplayName ?? '',
  });

  // eslint-disable-next-line arrow-body-style
  const disabledDate = (current) => {
    // Only dates in the past, including today
    return current > moment().endOf('day');
  };


  const disabled = !form.getFieldValue('manager') ||
    !form.getFieldValue('followUpBy') ||
    (form.getFieldsError() && Object.values(form.getFieldsError())?.filter(s => s)?.length);

  return (
    <>
      <SidePanel
        width={'40%'}
        title={actionTypeDescriptionState}
        onPanelClose={goToTicket}
        contentOverflow={false}
      >
        {isLoadingAction ?
          <Spinner /> :
          <Form layout='vertical'>
            <Row>
              <Col span={16}>
                <FormItem
                  label={intl.formatMessage({
                    id: 'ticket.smart_actions.TYPE',
                  })}
                >
                  {typeValidator(
                    <Dropdown
                      values={followUpTypes}
                      valueKey='id'
                      valueLabel='description'
                    />,
                  )}
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span={16}>
                <FormItem
                  label={intl.formatMessage({
                    id: 'ticket.smart_actions.ISSUE_DATE',
                  })}
                >
                  {sinceValidator(
                    <DatePicker
                      format='DD/MM/YYYY'
                      disabledDate={disabledDate}
                      allowClear={false}
                    />,
                  )}
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span={16}>
                <FormItem
                  label={intl.formatMessage({
                    id: 'ticket.smart_actions.REMINDER',
                  })}
                >
                  {cycleValidator(
                    <StyledInputNumber min={1} precision={0} />,
                  )}
                  <DaysSpan>
                    {intl.formatMessage({ id: 'ticket.smart_actions.DAYS_DYNAMIC' })}
                  </DaysSpan>
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span={16}>
                <FormItem
                  label={intl.formatMessage({
                    id: 'ticket.smart_actions.FOLLOW_UP_BY',
                  })}
                >
                  {followedByValidator(
                    <UsersDropdown
                      onChange={() => handleUserChange(false)}
                      onSelect={e => handleUserSelect(e, false)}
                      allowClear={true}
                      placeholder={
                        intl.formatMessage({ id: 'ticket.smart_actions.SELECT_FOLLOW_UP_BY' })
                      }
                    />,
                  )}
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span={16}>
                <FormItem
                  label={intl.formatMessage({
                    id: 'ticket.smart_actions.MANAGER',
                  })}
                >
                  {managerValidator(
                    <UsersDropdown
                      onChange={() => handleUserChange(true)}
                      onSelect={e => handleUserSelect(e, true)}
                      allowClear={true}
                      placeholder={
                        intl.formatMessage({ id: 'ticket.smart_actions.SELECT_MANAGER' })
                      }
                    />,
                  )}
                </FormItem>
              </Col>
            </Row>
            <Row>
              <StyledSaveButton
                onClick={goToTicket}
              >
                {intl.formatMessage({ id: 'ticket.smart_actions.CLOSE_SMART_ACTION' })}
              </StyledSaveButton>
              <StyledSaveButton
                loading={isSaving}
                onClick={() => match.params.actionId ? saveAction() : createAction()}
                type='secondary'
                disabled={disabled}
              >
                {intl.formatMessage({ id: 'ticket.smart_actions.SAVE_SMART_ACTION' })}
              </StyledSaveButton>
            </Row>
          </Form>
        }
      </SidePanel>
    </>
  );
};
FollowUpSidepanel.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  intl: PropTypes.object,
  form: PropTypes.object,
  ticket: PropTypes.object,
  saveSmartAction: PropTypes.func,
  createSmartAction: PropTypes.func,
  success: PropTypes.bool,
  isSaving: PropTypes.bool,
  action: PropTypes.object,
  fetchSmartAction: PropTypes.func,
  isLoadingAction: PropTypes.bool,
  followUpTypes: PropTypes.array,
};

export default Form.create()(injectIntl(withRouter(FollowUpSidepanel)));
